import { useContext } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

/**
 * Renders a visual preview for a file item inside a FileField itemTemplate.
 *
 * Must be used inside a FileField's itemTemplate — reads file data from the
 * nearest TemplateContext (set by FileField per-item).
 *
 * For image/* mime types: renders an <img> thumbnail with the file URL.
 * For all other types: renders a generic inline SVG file icon.
 *
 * YAML usage (inside itemTemplate):
 *   - type: FileFieldPreview
 *     attributes:
 *       class: w-10 h-10 object-cover rounded
 *       alt: ~.data.filename
 */
export const FileFieldPreview = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const attributes = useEvaluatedAttributes(props.attributes);

    const filemime = evaluateTemplateValue({
        valueToEvaluate: '~.data.filemime',
        globalDataContext,
        templateContext,
    }) || '';

    const url = evaluateTemplateValue({
        valueToEvaluate: '~.data.url',
        globalDataContext,
        templateContext,
    }) || null;

    const filename = evaluateTemplateValue({
        valueToEvaluate: '~.data.filename',
        globalDataContext,
        templateContext,
    }) || '';

    const isImage = filemime.startsWith('image/');

    if (isImage && url) {
        return (
            <ActionDependant {...props}>
                <img
                    src={url}
                    {...attributes}
                    alt={attributes?.alt ?? filename}
                />
            </ActionDependant>
        );
    }

    // Generic file icon SVG for non-image types or when URL is not yet available.
    const iconColor = isImage ? '#60a5fa' : '#94a3b8';

    return (
        <ActionDependant {...props}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke={iconColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-label={filename}
                role="img"
                {...attributes}
                style={{ width: '2.5rem', height: '2.5rem', ...(attributes?.style ?? {}) }}
            >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
            </svg>
        </ActionDependant>
    );
};

